import { useEffect, useState } from "react";
import { Lock, Check, ArrowRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { trackEvent } from "@/hooks/useEntitlements";

const fmt = (cents) => (cents == null ? "—" : `$${(cents / 100).toFixed(2)}`);

/**
 * Real entitlement-driven upgrade experience.
 * props: open, onClose, feature (key), requiredPlan, returnRoute, ent (entitlements payload)
 */
export function UpgradeModal({ open, onClose, feature, requiredPlan, returnRoute = "/business-credit", ent }) {
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/payments/catalog").then(({ data }) => setCatalog(data)).catch(() => setCatalog(null));
      trackEvent("upgrade_plan_viewed", { feature, plan: requiredPlan });
    }
  }, [open, feature, requiredPlan]);

  if (!open) return null;

  const currentPlan = ent?.plan || "Preview";
  const target = catalog?.plans?.find((p) => p.plan === requiredPlan);
  const current = catalog?.plans?.find((p) => p.plan === currentPlan);
  const priceDiff = target && current ? target.monthly_amount - current.monthly_amount : target?.monthly_amount;

  // Features unlocked at the target plan (locked now, become included at requiredPlan).
  const unlocks = Object.values(ent?.features || {}).filter(
    (f) => f.state === "upgrade_required" && f.required_plan === requiredPlan,
  );

  const startUpgrade = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post("/entitlements/upgrade-intent", { feature, return_route: returnRoute });
      localStorage.setItem("mmt_upgrade_return", returnRoute);
      trackEvent("checkout_started", { feature, plan: requiredPlan });
      const { data } = await api.post("/payments/checkout", {
        lookup_key: target.monthly_lookup,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.assign(data.checkout_url);
      else throw new Error("no url");
    } catch {
      toast.error("We couldn't start the upgrade. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4" data-testid="upgrade-modal">
      <div className="w-full max-w-lg overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-2 font-head text-[15px] font-extrabold text-slate-900">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#b0862f]/10 text-[#b0862f]"><Lock size={15} /></span>
            Upgrade your membership
          </div>
          <button onClick={onClose} data-testid="upgrade-modal-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-3 text-[13px]">
            <div className="rounded-lg border border-slate-200 px-3 py-2">
              <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Current plan</div>
              <div className="font-head text-[15px] font-bold text-slate-900" data-testid="upgrade-current-plan">{currentPlan}</div>
            </div>
            <ArrowRight size={16} className="text-slate-300" />
            <div className="rounded-lg border-2 border-[#1a5fd0] bg-[#1a5fd0]/5 px-3 py-2">
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">Recommended</div>
              <div className="font-head text-[15px] font-extrabold text-[#1a5fd0]" data-testid="upgrade-target-plan">{requiredPlan}</div>
            </div>
          </div>

          <div className="mt-5">
            <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500">Unlocks with {requiredPlan}</div>
            <ul className="mt-2 space-y-1.5">
              {unlocks.length ? unlocks.map((f) => (
                <li key={f.key} className="flex items-start gap-2 text-[13px] text-slate-700" data-testid="upgrade-unlock-item">
                  <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" /> <span><b>{f.name}</b> — {f.desc}</span>
                </li>
              )) : (
                <li className="text-[13px] text-slate-500">Access to the {requiredPlan} feature set.</li>
              )}
            </ul>
          </div>

          <div className="mt-5 flex items-end justify-between rounded-xl bg-slate-50 px-4 py-3">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{requiredPlan} membership</div>
              <div className="font-head text-2xl font-extrabold text-slate-900" data-testid="upgrade-price">
                {target ? fmt(target.monthly_amount) : "—"}<span className="text-[13px] font-medium text-slate-400">/mo</span>
              </div>
            </div>
            {priceDiff != null && priceDiff > 0 && current && (
              <div className="text-right text-[12px] text-slate-500">
                <div className="font-mono text-[9px] uppercase tracking-wider">Difference</div>
                <div className="font-semibold text-slate-700">+{fmt(priceDiff)}/mo</div>
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Billed monthly. Change takes effect immediately after payment is verified. Cancel anytime.</p>

          <button onClick={startUpgrade} disabled={busy || !target} data-testid="upgrade-continue-btn"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1a5fd0] px-5 py-3 font-head text-[14px] font-bold text-white transition-colors hover:bg-[#1550b8] disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Starting…</> : <>Continue Upgrade to {requiredPlan} <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
